import { QueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { lazy, Suspense, useMemo, useState } from "react";
import { z } from "zod";

import type { ProjectData } from "@grapesjs/studio-sdk";

import { frameQueryOptions } from "@/api/dragon-tiles";
import { useUpdateFrame } from "@/api/dragon-tiles";
import { ComingSoon } from "@/components/ComingSoon";
import { TextTileEditRoute } from "@/components/FrameCreateRoutes/TextTileEditRoute";
import { LoadingFallback } from "@/components/LoadingFallback";
import { getActiveFrameEventId } from "@/features/Connectors/frameEventMetadata";
import { connectorReturnSearchSchema } from "@/features/DragonTiles/connectorTileSelectReturn";
import { DuplicateEmailVariantOnlyModal } from "@/features/EmailEditor/components/DuplicateEmailVariantOnlyModal";
import defaultProjectData from "@/features/EmailEditor/grapes/defaultProjectData";
import { ConnectorMergeTagScopeProvider } from "@/features/TextComposerV2/extensions/mergeTags/ConnectorMergeTagScope";
import { updateFrameVariant } from "@/frames/service";
import { offerBlockQueryOptions } from "@/grapesPlugins/email/OfferBlock/hooks";
import { useGoBack } from "@/hooks/useGoBack";
import {
  CHANNEL_MEDIUMS,
  type EditorType,
  type Frame,
  type OffersConfig,
  type ProductGridConfig,
} from "@/types/dragon-tiles";
import { UNASSIGNED_DRAGON_TILE } from "@/types/dragon-tiles";

const EmailFrameBuilder = lazy(() =>
  import("@/features/EmailEditor/components/EmailFrameBuilder").then((m) => ({
    default: m.EmailFrameBuilder,
  })),
);
const HTMLFrameBuilder = lazy(() =>
  import("@/components/HTMLFrameBuilder/HTMLFrameBuilder").then((m) => ({
    default: m.HTMLFrameBuilder,
  })),
);
const TruePlaintextEmailBuilder = lazy(() =>
  import("@/components/TruePlaintextEmailBuilder/TruePlaintextEmailBuilder").then(
    (m) => ({ default: m.TruePlaintextEmailBuilder }),
  ),
);

const searchSchema = z.discriminatedUnion("returnTo", [
  z.object({
    returnTo: z.literal("tile"),
    parentTileId: z.string(),
    originTileId: z.string().optional(),
  }),
  z
    .object({
      returnTo: z.literal("connector-tile"),
      parentTileId: z.string(),
    })
    .merge(connectorReturnSearchSchema),
  z.object({
    returnTo: z.literal("creative-sphere"),
    medium: z.enum(CHANNEL_MEDIUMS).optional(),
    dragon_tile: z.string().optional(),
    event: z.string().optional(),
    order_by: z.enum(["create_date", "modify_date"]).optional(),
    order: z.enum(["asc", "desc"]).optional(),
    search: z.string().optional(),
    view: z.enum(["grid", "table"]).optional(),
    preview: z.string().optional(),
    deactivated: z.boolean().optional(),
  }),
  z.object({
    returnTo: z.literal("dragon-tiles"),
  }),
  z.object({
    returnTo: z.literal("broadcast-v2"),
    broadcastId: z.string().optional(),
  }),
]);

export const Route = createFileRoute(
  "/_auth/dragon-tiles/$siteId/$frameId/$variant",
)({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  loader: async ({ params, context }) => {
    const { queryClient } = context as {
      queryClient: QueryClient;
    };
    const { siteId, frameId, variant } = params;
    // Kick off JS chunk download in parallel with API data fetch so the editor
    // is ready by the time the data arrives (especially effective with intent preload on hover).
    if (variant === "email") {
      void import("@/features/EmailEditor/components/EmailFrameBuilder");
      void import("@/components/HTMLFrameBuilder/HTMLFrameBuilder");
      void queryClient.prefetchQuery(offerBlockQueryOptions(siteId));
    }
    await queryClient.ensureQueryData(frameQueryOptions(siteId, frameId));
  },
});

function RouteComponent() {
  const { variant, siteId, frameId } = Route.useParams();
  const search = Route.useSearch();
  const goBack = useGoBack();
  const parentTileId =
    search.returnTo === "tile"
      ? search.parentTileId
      : search.returnTo === "connector-tile"
        ? search.parentTileId
        : search.returnTo === "creative-sphere"
          ? // "unassigned" is a list filter value, not a tile id.
            search.dragon_tile === UNASSIGNED_DRAGON_TILE
            ? undefined
            : search.dragon_tile
          : undefined;

  if (variant === "text") {
    return (
      <TextTileEditRoute
        siteId={siteId}
        frameId={frameId}
        parentTileId={parentTileId}
        connectorId={
          search.returnTo === "connector-tile"
            ? search.connectorParentTileId
            : undefined
        }
        selectedEventId={
          search.returnTo === "connector-tile"
            ? search.selectedEventId
            : undefined
        }
        onCancel={goBack}
      />
    );
  }
  if (variant === "email") {
    return (
      // Key on frameId so switching tiles (e.g. after Duplicate & Open) fully
      // remounts the editor — the GrapesJS instance only reads its initial
      // project data on mount, so a prop change alone won't reload the content.
      <EmailEditRoute
        key={frameId}
        siteId={siteId}
        frameId={frameId}
        search={search}
        onCancel={goBack}
      />
    );
  }
  return <ComingSoon />;
}

interface EmailEditRouteProps {
  siteId: string;
  frameId: string;
  search: z.infer<typeof searchSchema>;
  onCancel?: () => void;
  onSaved?: () => void;
}

function EmailEditRoute({
  siteId,
  frameId,
  search,
  onCancel,
  onSaved,
}: EmailEditRouteProps) {
  const { data: frame } = useSuspenseQuery(frameQueryOptions(siteId, frameId));
  const updateFrameMutation = useUpdateFrame();
  const navigate = useNavigate();
  const isSaving = updateFrameMutation.isPending;
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const connectorMergeTagScope = useMemo(
    () => ({
      connectorId:
        search.returnTo === "connector-tile"
          ? search.connectorParentTileId
          : undefined,
      selectedEventId:
        (search.returnTo === "connector-tile"
          ? search.selectedEventId
          : undefined) ?? getActiveFrameEventId(frame.metadata),
    }),
    [frame.metadata, search],
  );

  // Find the email variant and extract its creative data
  const emailVariant = frame.frame_variants.find(
    (variant) => variant.medium === "email",
  );

  // Initialize editor content with existing email creative data or default layout
  const initialProjectData = emailVariant?.content_json || defaultProjectData;

  const handleCancel = () => {
    onCancel?.();
  };

  // Switch the editor to the newly duplicated tile.
  const openDuplicatedTile = (created: Frame) => {
    void navigate({
      to: "/dragon-tiles/$siteId/$frameId/$variant",
      params: { siteId, frameId: created.id, variant: "email" },
      search,
    });
  };

  const handleSave = async ({
    html,
    projectData,
    editorType,
    name,
    productGrids,
    offers,
  }: {
    html: string;
    projectData: ProjectData | null;
    editorType: EditorType;
    name: string;
    productGrids?: Record<string, ProductGridConfig>;
    offers?: OffersConfig;
  }) => {
    const payload = updateFrameVariant({
      frame,
      medium: "email",
      contentText: html,
      contentJson: projectData,
      editorType,
      config: { product_grids: productGrids, offers },
      createIfMissing: true,
      frameFields: { name },
    });
    await updateFrameMutation.mutateAsync({
      siteId,
      frameId: frame.id,
      payload,
    });

    onSaved?.();
  };

  const isLegacyHTMLEmail =
    emailVariant &&
    emailVariant.content_json &&
    Object.keys(emailVariant.content_json).length === 0;

  const isPlaintextEmail =
    emailVariant?.editor_type === "grapesjs_plaintext_email";

  if (emailVariant?.editor_type === "true_plaintext_email") {
    return (
      <ConnectorMergeTagScopeProvider scope={connectorMergeTagScope}>
        <Suspense fallback={<LoadingFallback />}>
          <TruePlaintextEmailBuilder
            siteId={siteId}
            innerTileId={frame.inner_tile}
            initialName={frame.name}
            initialSubject={emailVariant.config?.subject_line}
            initialPreheader={emailVariant.config?.preview_text}
            initialBody={emailVariant.content_plaintext ?? ""}
            isSaving={isSaving}
            onCancel={handleCancel}
            onSave={async ({
              name,
              subjectLine,
              previewText,
              body,
              offers,
            }) => {
              const payload = updateFrameVariant({
                frame,
                medium: "email",
                contentPlaintext: body,
                // A plaintext-only variant must never carry an HTML body
                contentText: "",
                contentJson: null,
                editorType: "true_plaintext_email",
                config: {
                  subject_line: subjectLine,
                  preview_text: previewText,
                  offers,
                },
                frameFields: { name },
              });
              await updateFrameMutation.mutateAsync({
                siteId,
                frameId: frame.id,
                payload,
              });
              onSaved?.();
            }}
          />
        </Suspense>
      </ConnectorMergeTagScopeProvider>
    );
  }

  if (isLegacyHTMLEmail || emailVariant?.editor_type === "html") {
    return (
      <ConnectorMergeTagScopeProvider scope={connectorMergeTagScope}>
        <Suspense fallback={<LoadingFallback />}>
          <HTMLFrameBuilder
            siteId={siteId}
            frameName={frame.name}
            frame={frame}
            onCancel={handleCancel}
            onSave={handleSave}
            initialProjectData={emailVariant?.content_text || ""}
            subjectLine={emailVariant?.config?.subject_line}
            preheader={emailVariant?.config?.preview_text}
            isSaving={isSaving}
          />
        </Suspense>
      </ConnectorMergeTagScopeProvider>
    );
  }

  return (
    <ConnectorMergeTagScopeProvider scope={connectorMergeTagScope}>
      <Suspense fallback={<LoadingFallback />}>
        <EmailFrameBuilder
          siteId={siteId}
          title={frame.name}
          frame={frame}
          initialProjectData={initialProjectData}
          subjectLine={emailVariant?.config?.subject_line}
          preheader={emailVariant?.config?.preview_text}
          isSaving={isSaving}
          onCancel={handleCancel}
          onSave={handleSave}
          mode={isPlaintextEmail ? "plaintext" : "full"}
          onDuplicate={
            emailVariant ? () => setIsDuplicateModalOpen(true) : undefined
          }
        />
        {emailVariant && (
          <DuplicateEmailVariantOnlyModal
            open={isDuplicateModalOpen}
            onClose={() => setIsDuplicateModalOpen(false)}
            frame={frame}
            siteId={siteId}
            onDuplicatedAndOpen={openDuplicatedTile}
          />
        )}
      </Suspense>
    </ConnectorMergeTagScopeProvider>
  );
}
